import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useProgressWithAutoRefresh } from "../../../shared/contexts/ProgressContext";
import { useStepRedirect, skipNextStepRedirect } from "../../../hooks/useStepRedirect";
import { ClosureApiService } from "../../closure/api/ClosureApiService";
import { normalizeAttachments } from "../../../shared/utils/attachmentUtils";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";

const GOVERNORATE_DISPLAY: Record<string, string> = {
  baghdad: "Baghdad",
  basra: "Basrah",
  nineveh: "Nineveh",
  maysan: "Maysan",
  diyala: "Diyala",
  anbar: "Al Anbar",
  kirkuk: "Kirkuk",
  saladin: "Saladin",
  dohuk: "Duhok",
  erbil: "Erbil",
  sulaymaniyah: "Sulaymaniyah",
  halabja: "Halabja",
  najaf: "Najaf",
  karbala: "Karbala",
  babil: "Babylon",
  wasit: "Wasit",
  qadisiyah: "Al Qadisiyah",
  muthanna: "Al Muthanna",
  dhi_qar: "Dhiqar",
};

const ClosureReviewScreen: React.FC = () => {
  useStepRedirect();

  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { progress } = useProgressWithAutoRefresh();
  const closureApi = useMemo(() => new ClosureApiService(), []);

  const [closureData, setClosureData] = useState<any>(null);
  const [documents, setDocuments] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const hasLoadedData = useRef(false);

  // Determine if this is a registered clinic closure or existing clinic closure
  const closureRequest = progress?.active_requests?.find(
    (r: any) => r.request_type === "closure"
  );
  const isRegisteredClosure = !!progress?.clinic_id;

  useEffect(() => {
    if (hasLoadedData.current || !progress || !closureRequest) return;
    hasLoadedData.current = true;

    const loadData = async () => {
      setLoading(true);
      try {
        const reqId = closureRequest.request_id;
        const detailResult = await closureApi.getClosureWithAttachments(reqId);
        if (detailResult.isSuccess && detailResult.data) {
          setClosureData(detailResult.data);
          const rawDocs = detailResult.data.attachments || detailResult.data.documents;
          setDocuments(normalizeAttachments(rawDocs));
        }
      } catch (error) {
        console.error("[ClosureReviewScreen] Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [closureRequest, closureApi]);

  const handleEditDetails = () => {
    skipNextStepRedirect();
    if (isRegisteredClosure) {
      navigate("/clinic-closure", { state: { fromReview: true } });
    } else {
      navigate("/existing-clinic-closure", { state: { fromReview: true } });
    }
  };

  const handleEditDocuments = () => {
    skipNextStepRedirect();
    navigate("/closure-documents", { state: { fromReview: true } });
  };

  const handleContinue = () => {
    skipNextStepRedirect();
    navigate("/closure-agree-terms");
  };

  const handleBack = () => {
    skipNextStepRedirect();
    navigate("/closure-documents");
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  if (!closureData) {
    return (
      <ResponsiveFormLayout
        title={t("review_application")}
        subtitle={t("error")}
        showLanguageToggle
        maxHeight="90vh"
      >
        <p className="text-center text-gray-600">{t("loading_failed")}</p>
      </ResponsiveFormLayout>
    );
  }

  // GOVERNORATE_DISPLAY maps the API enum to the English name, which is also the
  // translation key — without t() this renders "Baghdad" even in Arabic.
  const displayGovernorate = t(
    GOVERNORATE_DISPLAY[closureData.governorate] || closureData.governorate
  );

  const formatDocKey = (k: string) => {
    const translated = t(k);
    if (translated !== k) return translated;
    const titleKey = `${k}_title`;
    const titleTranslated = t(titleKey);
    if (titleTranslated !== titleKey) return titleTranslated;
    return k.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const getDocSortOrder = (k: string) => {
    if (k.includes("signboard")) return 1;
    if (k.includes("rooms")) return 2;
    return 3;
  };

  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: string | number | null | undefined;
  }) => {
    if (value === null || value === undefined || value === "") return null;
    return (
      <div
        className="flex justify-between items-start py-2 border-b border-gray-100"
      >
        <span className="text-sm text-gray-500">{label}</span>
        <span className="text-sm text-gray-900 font-medium">{value}</span>
      </div>
    );
  };

  const SectionHeader = ({
    title,
    onEdit,
  }: {
    title: string;
    onEdit: () => void;
  }) => (
    <div
      className="flex items-center justify-between mb-3"
    >
      <h3 className="text-base font-bold text-gray-800">{title}</h3>
      <button
        onClick={onEdit}
        className="text-sm font-semibold text-[#2E5D7D] hover:underline"
      >
        {t("edit")}
      </button>
    </div>
  );

  const continueButton = (
    <div className="flex gap-3 w-full">
      <button
        onClick={handleBack}
        className="min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
      >
        {t("back") || "Back"}
      </button>
      <button
        onClick={handleContinue}
        className="flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 shadow-md hover:shadow-lg hover:opacity-90"
        style={{ backgroundColor: "#2E5D7D" }}
      >
        {t("continue")}
      </button>
    </div>
  );

  return (
    <>
    <ResponsiveFormLayout
      onBack={handleBack}
      showBackButton={true}
      showLanguageToggle
      icon="❌"
      iconSize="w-24 h-24 text-6xl"
      title={t("review_application")}
      subtitle={t("review_subtitle")}
      actions={continueButton}
      maxHeight="80vh"
    >
      <div className="w-full space-y-5">
        {/* Closure Details Section */}
        <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-red-50 to-orange-50">
          <SectionHeader
            title={t("closure_details") || "Closure Details"}
            onEdit={handleEditDetails}
          />
          <div className="bg-white p-4 rounded-lg space-y-0">
            {/* Show clinic info for existing closure */}
            {!isRegisteredClosure && (
              <>
                <InfoRow
                  label={t("clinic_name")}
                  value={closureData.name || closureData.clinic?.name}
                />
                <InfoRow label={t("governorate")} value={displayGovernorate} />
                <InfoRow label={t("city")} value={isRTL ? closureData.area?.name_ar : closureData.area?.name} />
                <InfoRow
                  label={t("neighborhood")}
                  value={closureData.neighborhood}
                />
                <InfoRow label={t("street")} value={closureData.street} />
                <InfoRow
                  label={t("house_number")}
                  value={closureData.house_number}
                />
                <InfoRow
                  label={t("nearest_landmark")}
                  value={closureData.nearest_landmark}
                />
                <InfoRow
                  label={t("phone")}
                  value={closureData.phone_primary}
                />
              </>
            )}
            <InfoRow
              label={t("closure_reason") || "Closure Reason"}
              value={closureData.closure_reason}
            />
            <InfoRow
              label={t("closure_date") || "Closure Date"}
              value={closureData.closure_date}
            />
            {!closureData.closure_reason && !closureData.closure_date && (
              <p className="text-sm text-gray-400 py-2">
                {t("no_details_provided") || "No additional details provided"}
              </p>
            )}
          </div>
        </div>

        {/* Documents Section */}
        <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <SectionHeader
            title={t("uploaded_documents")}
            onEdit={handleEditDocuments}
          />
          {Object.keys(documents).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(documents)
                .sort(([a], [b]) => getDocSortOrder(a) - getDocSortOrder(b))
                .map(([key, value]) => {
                  if (!value) return null;

                  if (Array.isArray(value)) {
                    return (
                      <div key={key} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-sm font-semibold text-gray-900">
                            {formatDocKey(key)} ({value.length})
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {value.map((url: string, index: number) => (
                            <div
                              key={index}
                              onClick={() => setSelectedImage(url)}
                              className="relative cursor-pointer group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#2E5D7D] transition-all"
                            >
                              <img
                                src={url}
                                alt={`${formatDocKey(key)} ${index + 1}`}
                                className="w-full h-32 object-cover"
                                loading="lazy"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                <span className="text-white text-xs font-medium">
                                  {formatDocKey(key)} {index + 1}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-900">{formatDocKey(key)}</span>
                      </div>
                      <div
                        onClick={() => setSelectedImage(value)}
                        className="relative cursor-pointer group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#2E5D7D] transition-all"
                      >
                        <img
                          src={value}
                          alt={formatDocKey(key)}
                          className="w-full h-40 object-cover"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="bg-white p-4 rounded-lg">
              <p className={`text-sm text-gray-400 ${isRTL ? "text-right" : "text-left"}`}>
                {t("not_uploaded")}
              </p>
            </div>
          )}
        </div>
      </div>
    </ResponsiveFormLayout>

    {/* Image Lightbox */}
    {selectedImage && (
      <div
        className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center p-4"
        style={{ zIndex: 9999999 }}
        onClick={() => setSelectedImage(null)}
      >
        <button
          onClick={() => setSelectedImage(null)}
          className="absolute top-4 left-4 text-white text-4xl hover:text-gray-300 transition-all z-10 flex items-center gap-2"
        >
          <span>←</span>
          <span className="text-lg font-semibold">{t("close")}</span>
        </button>
        <img
          src={selectedImage}
          alt={t("document_preview")}
          className="max-w-full max-h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>
  );
};

export default ClosureReviewScreen;
